import React, { useMemo } from 'react';
import { useClientUser } from '@/hooks';
import { carrierDisplayName } from '@/utilities/carrierAlias';
import { colors } from '@/styles';
import { css } from '@emotion/react';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import FlagIcon from '@material-ui/icons/Flag';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import {
    EXCEPTION_DISPLAY,
    EXCEPTION_TYPE_DISPLAY_MAPPING,
    EXCEPTION_STATUS_DISPLAY_MAPPING,
} from '@onward-delivery/core';
import { BodyText } from '@/components/CarrierAccountingOrders/blocks';

const dateShort = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

export const FILTERABLE_COLUMN_IDS = [
    'exception_time',
    'exception_type',
    'exception_occured_at',
    'shipper_name',
    'carrier_name',
];

// A hook rather than a module constant so the carrier column can resolve the viewing
// shipper's alias, matching the useColumns idiom the other tables in this app use.
export const useColumns = () => {
    const { partnerCarriersById } = useClientUser();

    return useMemo(
        () => [
            {
                id: 'flag',
                sticky: 'left',
                width: 60,
                minWidth: 60,
                maxWidth: 60,
                Header: () => null,
                disableSortBy: true,
                disableResizing: false,
                Cell: ({ row, callbacks }) => {
                    const mapping = row.original;
                    const order = mapping?.order;
                    const exception = mapping?.exception;
                    const user_id = callbacks?.user_id;
                    const carrierAction =
                        !order?.oms && exception?.status === 'PENDING_CONFIRMATION' && user_id === order?.carrier_id;
                    const shipperAction = user_id === order?.shipper_id && (order?.oms || exception?.status === 'OPEN');

                    if (carrierAction || shipperAction) {
                        return (
                            <FlagIcon
                                css={css`
                                    color: #d23e3e;
                                `}
                            />
                        );
                    }
                    return <FlagOutlinedIcon />;
                },
            },
            {
                Header: 'Data',
                id: 'data-columns',
                columns: [
                    {
                        Header: 'Order',
                        id: 'order_number',
                        accessor: (exception) => exception.order.order_number,
                        disableSortBy: true,
                    },
                    {
                        Header: 'PO',
                        id: 'po_number',
                        accessor: (exception) => (exception.order.po_number ? exception.order.po_number : '--'),
                        disableSortBy: true,
                    },
                    {
                        Header: 'Items',
                        id: 'sku',
                        accessor: (exception) => {
                            return exception?.item?.length > 0
                                ? exception.item.map((item) => item.sku).join(', ')
                                : '--';
                        },
                        disableSortBy: true,
                    },
                    {
                        Header: 'Shipper',
                        id: 'shipper_name',
                        accessor: (exception) => exception?.order?.order_shipper?.business_name || '--',
                        disableSortBy: true,
                    },
                    {
                        Header: 'Carrier',
                        id: 'carrier_name',
                        accessor: (exception) =>
                            carrierDisplayName(exception?.order?.order_carrier, partnerCarriersById) || '--',
                        disableSortBy: true,
                    },
                    {
                        Header: 'Exception Entity',
                        id: 'exception_entity',
                        accessor: (exception) => EXCEPTION_TYPE_DISPLAY_MAPPING[exception?.exception_type] || '--',
                        disableSortBy: true,
                    },
                    {
                        Header: 'Type',
                        id: 'exception_type',
                        accessor: (exception) => EXCEPTION_DISPLAY[exception?.exception?.type] || '--',
                        disableSortBy: true,
                    },
                    {
                        Header: 'Occurred',
                        id: 'exception_occured_at',
                        accessor: (exception) => exception?.exception?.reported_at || '--',
                        disableSortBy: true,
                    },
                    {
                        Header: 'Reported At',
                        id: 'exception_time',
                        accessor: (exception) => new Date(exception?.created_at).getTime(),
                        Cell: ({ value }) => {
                            return value ? dateShort.format(new Date(value)) : '--';
                        },
                    },
                    {
                        Header: 'Order Type',
                        id: 'order_type',
                        accessor: (exception) => (exception?.order?.oms ? 'Internal' : 'Marketplace'),
                        disableSortBy: true,
                    },
                ],
            },
            {
                sticky: 'right',
                Header: 'Sticky',
                id: 'sticky-group',
                columns: [
                    {
                        Header: 'Status',
                        id: 'exception_status',
                        width: 160,
                        Cell: ({ row }) => {
                            const exception = row.original;
                            const label =
                                exception.exception.status === 'OPEN' &&
                                exception.exception.type === 'generalIntegrationException'
                                    ? 'Req Attention'
                                    : EXCEPTION_STATUS_DISPLAY_MAPPING[exception.exception.status];

                            let fragment = css`
                                color: ${colors.greens.primary};
                            `;
                            let icon = (
                                <CheckCircleIcon
                                    css={css`
                                        margin-right: 8px;
                                        ${fragment}
                                    `}
                                />
                            );
                            if (exception.exception.status !== 'RESOLVED') {
                                fragment = css`
                                    color: #d23e3e;
                                `;
                                icon = (
                                    <HighlightOffIcon
                                        css={css`
                                            margin-right: 8px;
                                            ${fragment}
                                        `}
                                    />
                                );
                            }

                            return (
                                <div
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        white-space: nowrap;
                                    `}
                                >
                                    {icon}
                                    <BodyText
                                        css={css`
                                            font-weight: 700;
                                            ${fragment};
                                        `}
                                    >
                                        {label}
                                    </BodyText>
                                </div>
                            );
                        },
                        canSort: false,
                        span: false,
                    },
                    {
                        id: 'show_hidden_columns',
                        Header: '',
                        accessor: () => '',
                        width: 52,
                        minWidth: 52,
                        maxWidth: 52,
                        disableSortBy: true,
                        disableResizing: true,
                    },
                ],
            },
        ],
        [partnerCarriersById]
    );
};
